import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Armchair, Briefcase, ChevronDown, History, Loader2, Luggage, Minus, Plane, Plus, Sparkles, TriangleAlert, Zap } from "lucide-react";
import { operatingDate, staleTzRules, type Disruption, type ExtendResource } from "@yb/core";
import { api, ApiError, canPlan as roleCanPlan, isAdmin, isVendor, onSignedOut, setSandbox, type ClockState, type OptimizeDayPreview, type OptimizePreview, type RecoveryPreview, type User } from "./lib/api";
import { dayToOpen } from "./lib/planning";
import { airlineName, hhmm, textedText } from "./lib/format";
import { useBoard, useCameraTurns, useConfig, useLiveUpdates, useLocalStorage, useNow, useSession } from "./lib/hooks";
import { Timeline, type BarLook, type TimelinePreview } from "./components/Timeline";
import { TopBar } from "./components/TopBar";
import { QualityRing, ScoreKpis } from "./components/Kpis";
import { AlertsPanel, FlightPanel } from "./components/SidePanel";
import { PHONE_MAX_WIDTH, useDevice } from "./lib/device";
import { OptimizeDaysMenu, OptimizeDaysSheet, OptimizeSheet, RecoverySheet } from "./components/Sheets";
import { CloseStandDialog, VersionHistory } from "./components/Dialogs";
import { Rules } from "./components/Rules";
import { Login } from "./components/Login";
import { Reports } from "./components/Reports";
import { Schedule } from "./components/Schedule";
import { Help } from "./components/Help";
import { Bags } from "./components/Bags";
import { Forecast } from "./components/Forecast";
import { RampView } from "./components/RampView";
import { ResourceTimeline, movedCounters, resourceView, type ResItem, type ResourceKind } from "./components/ResourceTimeline";
import { EXTEND_WHAT, extendSpan, extendTarget } from "./lib/extend";
import { CancelDialog, ContextMenu, ExtendDialog, HoverCard, MechDialog, SwapDialog, TowDialog, cancelledById, type MenuActions } from "./components/OpsMenus";
import { SettingsPage, type Section as SettingsSection } from "./components/Settings";
import { VendorPage } from "./components/VendorPage";
import { Support } from "./components/Support";
import { SandboxBanner, SandboxPage } from "./components/Sandbox";

type Mode =
  | null
  | { type: "optimize"; preview: OptimizePreview | null }
  | { type: "optimize-days"; count: number; days: OptimizeDayPreview[] | null }
  | { type: "recovery"; disruption: Disruption; preview: RecoveryPreview | null; selected: string | null };

interface Toast {
  text: string;
  tone: "ok" | "bad";
  undo?: boolean;
  /** How many plan versions the change made, which is how many undos take it back. */
  undoSteps?: number;
  /** A planner action the change recorded (an extension), taken back by Undo. */
  undoEvent?: string;
  /** A move a planner made by hand: the toast asks why, so the plan can learn from it. */
  reasonFor?: { turnId: string; standId: string | null };
}

/** The reasons a duty manager gives most, one tap each. */
export const MOVE_REASONS = ["Handler's equipment", "Jet bridge needed", "Customs", "Airline asked", "Room for another flight", "Tow or parking"];

export function App() {
  const session = useSession();
  const qc = useQueryClient();
  const [dark, setDark] = useLocalStorage("yb.dark", window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false);
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);
  // The session is read once and trusted until a call says otherwise.
  useEffect(() => {
    onSignedOut(() => void qc.invalidateQueries({ queryKey: ["session"] }));
    return () => onSignedOut(null);
  }, [qc]);

  if (session.isLoading) return <FullScreenSpinner />;
  if (!session.data?.user) return <Login demo={session.data?.demoLogin ?? null} pending={session.data?.pending ?? null} />;
  // A vendor has no board: the server answers 403 to everything but the API page's own calls.
  if (isVendor(session.data.user.role)) return <VendorPage user={session.data.user} dark={dark} setDark={setDark} />;
  return <Workspace user={session.data.user} dark={dark} setDark={setDark} />;
}

function FullScreenSpinner() {
  return (
    <div className="grid h-full place-items-center">
      <Loader2 className="size-6 animate-spin text-ink-3" />
    </div>
  );
}

const ZOOMS = [1.4, 2.4, 3.6];

function Workspace({ user, dark, setDark }: { user: User; dark: boolean; setDark: (v: boolean) => void }) {
  const qc = useQueryClient();
  const session = useSession();
  const canPlan = roleCanPlan(user.role);
  const [date, setDate] = useState<string | undefined>(undefined);
  // Live or a sandbox. Set on the API module before any query below reads it, so the board,
  // the plan and Settings all answer for the same one. Administrators only.
  const [sandboxPick, setSandboxPick] = useLocalStorage<number | null>("yb.sandbox", null);
  const inSandbox = isAdmin(user.role) ? sandboxPick : null;
  setSandbox(inSandbox);
  const pickSandbox = (id: number | null) => {
    setSandbox(id);
    setSandboxPick(id);
    // What was read for the other one is not this one's: read everything again.
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "session" });
  };
  const sandboxes = useQuery({ queryKey: ["sandboxes"], queryFn: () => api.sandboxes(), enabled: isAdmin(user.role) });
  const sandboxNow = sandboxes.data?.sandboxes.find((s) => s.id === inSandbox && s.status === "active") ?? null;
  useEffect(() => {
    // A sandbox archived, or not yet approved, is not somewhere to stay.
    if (inSandbox != null && sandboxes.data && !sandboxNow) pickSandbox(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inSandbox, sandboxes.data, sandboxNow]);
  const board = useBoard(date);
  const cameras = useCameraTurns(board.data?.date);
  const config = useConfig(true);
  const [clock, setClock] = useState<ClockState | undefined>(undefined);
  const live = useLiveUpdates(setClock, inSandbox);
  const now = useNow(clock ?? board.data?.clock);
  const device = useDevice();
  // A phone opens on the ramp cards; a tablet, upright or not, gets the board.
  const [view, setView] = useState<"board" | "ramp" | "settings" | "history" | "schedule" | "bags" | "forecast" | "rules" | "support" | "sandbox">(() => (window.innerWidth < PHONE_MAX_WIDTH || user.role === "handler" ? "ramp" : "board"));
  // The screen the person was on when they opened Support, pre-filled on the form.
  const [supportFrom, setSupportFrom] = useState("board");
  const [selected, setSelected] = useState<string | null>(null);
  const [focusAt, setFocusAt] = useState<number | null>(null);
  const [help, setHelp] = useState(false);
  // How bars are painted is the reader's choice, and it follows them between visits.
  const [look, setLook] = useLocalStorage<BarLook>("yb.board.look", "airline");
  const [mode, setMode] = useState<Mode>(null);
  const [busy, setBusy] = useState(false);
  const [closeStand, setCloseStand] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [optimizeMenu, setOptimizeMenu] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [zoom, setZoom] = useLocalStorage("yb.zoom", 1);
  const [boardView, setBoardView] = useLocalStorage<"stands" | ResourceKind>("yb.boardView", "stands");
  const [hover, setHover] = useState<{ turnId: string; x: number; y: number } | null>(null);
  const [ctx, setCtx] = useState<{ turnId: string; x: number; y: number; item: ResItem | null } | null>(null);
  const [towFor, setTowFor] = useState<string | null>(null);
  const [mechFor, setMechFor] = useState<string | null>(null);
  const [extendFor, setExtendFor] = useState<{ turnId: string; resource: ExtendResource } | null>(null);
  const [swapFor, setSwapFor] = useState<string | null>(null);
  /** A turn whose flight is being cancelled, or a C1 flight whose bags are being changed. */
  const [cancelFor, setCancelFor] = useState<string | null>(null);
  // Which Settings section to open on, when something else in the app sends people there.
  const [settingsSection, setSettingsSection] = useState<SettingsSection | undefined>(undefined);

  const b = board.data;
  const cfg = config.data;
  const tz = b?.airport.timezone ?? "UTC";
  const today = b ? operatingDate(now, tz) : "";
  const activeDate = b?.date ?? date ?? "";
  // Times are formatted here with the browser's own time zone tables, a second source
  // the server cannot vouch for; an outdated device shows every winter time an hour out.
  const browserTz = useMemo(() => (b ? staleTzRules(tz)[0] : undefined), [b, tz]);
  const warnings = [
    session.data?.storageWarning,
    session.data?.timeWarning,
    browserTz && `This device's time zone data predates ${browserTz.rule}: from ${browserTz.from} the times shown here would be an hour out. Update the browser or the device's operating system.`,
  ].filter((w): w is string => !!w);

  // Late in the day there is little left to plan: open tomorrow instead (once, and only on the board).
  const autoForward = useRef(false);
  useEffect(() => {
    if (!b || autoForward.current || date !== undefined || view !== "board") return;
    autoForward.current = true;
    const next = dayToOpen(b, view);
    if (next) setDate(next);
  }, [b, date, view]);
  // The ramp list is about the next few hours, so opening it means today.
  const changeView = (v: typeof view) => {
    if (v === "ramp" && v !== view) setDate(undefined);
    if (v !== "settings") setSettingsSection(undefined);
    if (v === "support" && view !== "support") setSupportFrom(view);
    setView(v);
  };
  const openSupport = (from: string) => {
    setHelp(false);
    setSupportFrom(from);
    setView("support");
  };

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), toast.reasonFor ? 15000 : toast.undo || toast.undoEvent ? 9000 : 5000);
    return () => clearTimeout(id);
  }, [toast]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["board"] });
  const fail = (err: unknown) => {
    setToast({ text: err instanceof ApiError ? err.message : "Something went wrong", tone: "bad" });
    if (err instanceof ApiError && err.status === 409) void refresh();
  };

  // The toast can be written from what the server answered, such as who was texted.
  const run = async <T,>(fn: () => Promise<T>, success?: Toast | ((result: T) => Toast)) => {
    setBusy(true);
    try {
      const result = await fn();
      await refresh();
      if (success) setToast(typeof success === "function" ? success(result) : success);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const startOptimize = async () => {
    setSelected(null);
    setMode({ type: "optimize", preview: null });
    try {
      const preview = await api.optimizePreview(activeDate);
      setMode({ type: "optimize", preview });
    } catch (err) {
      setMode(null);
      fail(err);
    }
  };

  /** The day on the board and the days after it, each previewed before anything changes. */
  const startOptimizeDays = async (count: number) => {
    setOptimizeMenu(false);
    if (count === 1) return startOptimize();
    setSelected(null);
    setMode({ type: "optimize-days", count, days: null });
    try {
      const r = await api.optimizePreviewDays(activeDate, count);
      setMode({ type: "optimize-days", count, days: r.days });
    } catch (err) {
      setMode(null);
      fail(err);
    }
  };

  const startRecovery = async (disruption: Disruption, day?: string) => {
    setCloseStand(null);
    const forDay = day ?? activeDate;
    if (forDay !== activeDate) {
      setDate(forDay === today ? undefined : forDay);
      setSelected(null);
    }
    setMode({ type: "recovery", disruption, preview: null, selected: null });
    try {
      const preview = await api.recoveryPreview(forDay, disruption);
      const first = preview.options.find((o) => o.id === "minimal") ?? preview.options[0];
      setMode({ type: "recovery", disruption, preview, selected: first?.id ?? null });
      if (disruption.kind === "stand-closure") setFocusAt(disruption.from);
    } catch (err) {
      setMode(null);
      fail(err);
    }
  };

  const timelinePreview: TimelinePreview | null = useMemo(() => {
    if (mode?.type === "optimize" && mode.preview) {
      return { stands: mode.preview.after.stands, moved: new Set(mode.preview.diff.moves.map((m) => m.turnId)) };
    }
    if (mode?.type === "optimize-days" && mode.days) {
      const d = mode.days.find((x) => x.date === activeDate);
      if (d) return { stands: d.after.stands, moved: new Set(d.diff.moves.map((m) => m.turnId)) };
    }
    if (mode?.type === "recovery" && mode.preview && mode.selected) {
      const o = mode.preview.options.find((x) => x.id === mode.selected);
      if (o) return { stands: o.stands, moved: new Set(o.moves.map((m) => m.turnId)), closures: mode.preview.closures, turns: mode.preview.turns };
    }
    return null;
  }, [mode, activeDate]);

  if (!b || !cfg) return <FullScreenSpinner />;

  const selectedTurn = selected ? b.turns.find((t) => t.id === selected) : undefined;
  /**
   * Hold the stand, desks or a belt for a flight until `until`. Undo in the toast takes a
   * new extension back; one that replaced an earlier extension is changed again from the
   * menu instead, since taking it back would leave no extension at all.
   */
  const extendTo = (resource: ExtendResource, turnId: string, until: number, note?: string) => {
    const t = b.turns.find((x) => x.id === turnId);
    const span = t && extendSpan(b, resource, t);
    // Dragged or set back to where it ends anyway: that is taking the extension off.
    if (span && until <= span.usual) {
      if (span.ext) menuActions.removeEvent(span.ext.eventId);
      return;
    }
    const keepNote = note ?? span?.ext?.note;
    void run(
      () => api.extend(activeDate, turnId, resource, until, keepNote || undefined),
      (r) => ({ text: r.summary, tone: "ok", ...(span?.ext ? {} : { undoEvent: r.id }) }),
    );
  };
  const menuActions: MenuActions = {
    explain: (turnId) => setSelected(turnId),
    move: (turnId, standId) => run(() => api.move(activeDate, b.version.id, turnId, standId), { text: `Moved to ${standId ?? "unallocated"}`, tone: "ok", undo: true }),
    lock: (turnId, locked) =>
      run(() => api.move(activeDate, b.version.id, turnId, b.plan.stands[turnId]?.standId ?? null, locked), { text: locked ? "Locked" : "Unlocked", tone: "ok", undo: true }),
    tow: (turnId) => setTowFor(turnId),
    mech: (turnId) => setMechFor(turnId),
    extend: (resource, turnId, until) => (until === undefined ? setExtendFor({ turnId, resource }) : extendTo(resource, turnId, until)),
    swapTails: (turnId) => setSwapFor(turnId),
    split: (turnId) => run(() => api.splitTurn(activeDate, turnId), { text: "Arrival and departure split", tone: "ok" }),
    removeEvent: (id) => {
      const ev = b.events.find((e) => e.id === id);
      const turn = ev && b.turns.find((t) => (ev.arrLegId && t.arr?.id === ev.arrLegId) || (ev.depLegId && t.dep?.id === ev.depLegId));
      void run(
        () => api.removeEvent(id),
        (r) => ({
          text:
            ev?.kind === "tow"
              ? `Tow removed. ${textedText(r.names, turn ? airlineName(turn.airline, cfg) : "this airline")}`
              : ev?.kind === "extend"
                ? `${ev.label ?? "Flight"} ${EXTEND_WHAT[ev.resource].toLowerCase()} back to its usual end`
                : ev?.kind === "cancel"
                  ? `${ev.label ?? "The flight"} is no longer cancelled here`
                : "Removed",
          tone: "ok",
        }),
      );
    },
    cancelFlight: (turnId) => setCancelFor(turnId),
    bags: (cancelledId) => setCancelFor(cancelledId),
    disrupt: (d) => startRecovery(d),
    setResource: (kind, legIds, value, clear) =>
      run(
        async () => {
          // Each flight is its own version, made on the one the flight before it produced.
          let base = b.version.id;
          for (const legId of legIds) {
            const v = typeof value === "function" ? value(b.plan.counters[legId]?.counters ?? []) : value;
            base = (await api.setResource(activeDate, base, kind, legId, v, clear)).versionId;
          }
        },
        { text: clear ? "Back to automatic" : legIds.length > 1 ? `Updated ${legIds.length} flights` : "Updated", tone: "ok", undo: true, undoSteps: legIds.length },
      ),
  };
  /** The selected flight's panel: beside the board, over it on a tablet, full screen on a phone. */
  const flightPanel = selectedTurn ? (
    <FlightPanel
      key={selectedTurn.id}
      board={b}
      config={cfg}
      turn={selectedTurn}
      canEdit={canPlan && mode === null}
      onClose={() => setSelected(null)}
      onMove={(standId) =>
        run(() => api.move(activeDate, b.version.id, selectedTurn.id, standId), {
          text: `Moved to ${standId}`,
          tone: "ok",
          undo: true,
          reasonFor: { turnId: selectedTurn.id, standId },
        })
      }
      onLock={(locked) => run(() => api.move(activeDate, b.version.id, selectedTurn.id, b.plan.stands[selectedTurn.id]?.standId ?? null, locked))}
      onDisrupt={startRecovery}
      onChangeType={(type) => run(() => api.changeType(activeDate, selectedTurn.id, type), { text: `Aircraft type set to ${type}`, tone: "ok" })}
      onRemove={(note) => {
        setSelected(null);
        void run(() => api.removeFlight(activeDate, selectedTurn.id, note), { text: "Removed from the board. Put it back from History.", tone: "ok" });
      }}
      onRules={() => setView("rules")}
      cameras={cameras.data?.turns}
      onExtend={(resource) => menuActions.extend(resource, selectedTurn.id)}
      onCancelFlight={() => setCancelFor(selectedTurn.id)}
    />
  ) : null;
  const offBoard = b.events.flatMap((e) => (e.kind === "remove" ? [{ id: e.id, label: e.label ?? "A flight", note: e.note }] : []));
  // Allocations held past their usual end, listed in History with a way to take each back.
  const extensions = b.events.flatMap((e) =>
    e.kind === "extend"
      ? [{ id: e.id, label: `${e.label ?? "A flight"} ${EXTEND_WHAT[e.resource].toLowerCase()} extended to ${hhmm(e.until, tz)}`, note: [e.note, e.by && `by ${e.by}`].filter(Boolean).join(" · ") || undefined }]
      : [],
  );
  const res = boardView === "stands" ? null : resourceView(b, cfg, boardView);
  const tabCounts = {
    stands: b.turns.length,
    counters: Object.keys(b.plan.counters).length,
    belts: Object.keys(b.plan.belts).length,
    outbound: Object.keys(b.plan.outbound ?? {}).length,
  };
  const score = b.score;
  const nextPredictions = b.predictions.length;

  return (
    <div className="flex h-full flex-col">
      <TopBar
        board={b}
        user={user}
        now={now}
        today={today}
        date={activeDate}
        onDate={(d) => {
          setDate(d === today ? undefined : d);
          setSelected(null);
          setMode(null);
        }}
        onNow={() => {
          setDate(undefined);
          // A new value each click, so pressing Now again scrolls back even if it was already there.
          setFocusAt(now === focusAt ? now + 1 : now);
        }}
        live={live}
        dark={dark}
        onDark={setDark}
        look={look}
        onLook={setLook}
        onClock={(action, speed) => run(() => api.clock(action, { speed }))}
        onLogout={async () => {
          await api.logout();
          await qc.invalidateQueries();
        }}
        view={view}
        onView={changeView}
        onHelp={() => setHelp(true)}
        canPlan={canPlan}
        sandbox={isAdmin(user.role) ? (sandboxNow ? { id: sandboxNow.id, name: sandboxNow.name } : "live") : null}
      />

      {sandboxNow && <SandboxBanner name={sandboxNow.name} onLive={() => pickSandbox(null)} onOpen={() => changeView("sandbox")} />}
      {warnings.map((w) => (
        <div key={w} className="flex items-center gap-2 border-b border-bad/40 bg-bad/10 px-4 py-1.5 text-[12.5px] text-bad print:hidden">
          <TriangleAlert className="size-4 shrink-0" />
          <span>{w}</span>
        </div>
      ))}
      {help && <Help onClose={() => setHelp(false)} role={user.role} onSupport={() => openSupport(view === "support" ? supportFrom : view)} />}
      {view === "sandbox" ? (
        <SandboxPage current={inSandbox} onPick={pickSandbox} onBoard={() => setView("board")} />
      ) : view === "rules" ? (
        <Rules onBack={() => setView("board")} />
      ) : view === "support" ? (
        <Support user={user} tz={tz} from={supportFrom} />
      ) : view === "forecast" ? (
        <Forecast today={today} tz={b?.airport.timezone ?? "UTC"} config={cfg} canUpload={canPlan} />
      ) : view === "bags" ? (
        <Bags today={today} tz={b?.airport.timezone ?? "UTC"} canUpload={canPlan} />
      ) : view === "schedule" ? (
        <Schedule config={cfg} today={today} canEdit={canPlan} />
      ) : view === "history" ? (
        <Reports
          tz={b?.airport.timezone ?? "UTC"}
          today={today}
          isAdmin={isAdmin(user.role)}
          seesCheckpoint={user.role !== "airline"}
          config={cfg}
          printedBy={user.name}
          onOpenDay={(d) => {
            setDate(d);
            setView("board");
            setHistoryOpen(true);
          }}
        />
      ) : view === "settings" ? (
        <SettingsPage
          canEdit={isAdmin(user.role)}
          isSuper={user.role === "superadmin"}
          onSaved={() => { void qc.invalidateQueries(); }}
          onRules={() => setView("rules")}
          initialSection={settingsSection}
          planDate={activeDate}
          sandbox={inSandbox}
        />
      ) : view === "ramp" ? (
        <>
          <RampView board={b} config={cfg} onSelect={setSelected} cameras={cameras.data?.turns} />
          {flightPanel && <div className="fixed inset-0 z-40 bg-panel">{flightPanel}</div>}
        </>
      ) : (
        <>
          <div className="flex shrink-0 flex-wrap items-center gap-x-6 gap-y-2 border-b border-line bg-panel px-4 py-2.5">
            {score ? (
              <>
                <div className="flex items-center gap-2.5">
                  <QualityRing value={score.quality} />
                  <div className="leading-tight">
                    <div className="text-[10.5px] font-medium tracking-wide text-ink-3 uppercase">Plan quality</div>
                    <div className="max-w-56 truncate text-[12px] text-ink-2" title={b.version.summary}>
                      v{b.version.id} · {b.version.summary}
                    </div>
                  </div>
                </div>
                <ScoreKpis score={score} predicted={activeDate === today ? nextPredictions : undefined} />
              </>
            ) : (
              <div className="text-[13px] text-ink-2">
                Showing <b>{cfg.airlines[user.airline ?? ""]?.name ?? user.airline}</b> flights only · read-only
              </div>
            )}
            <div className="ml-auto flex items-center gap-2">
              <div className="flex items-center rounded-lg border border-line">
                <button className="p-1.5 text-ink-2 hover:text-ink disabled:opacity-40" disabled={zoom === 0} onClick={() => setZoom(Math.max(0, zoom - 1))} aria-label="Zoom out">
                  <Minus className="size-4" />
                </button>
                <button className="p-1.5 text-ink-2 hover:text-ink disabled:opacity-40" disabled={zoom === ZOOMS.length - 1} onClick={() => setZoom(Math.min(ZOOMS.length - 1, zoom + 1))} aria-label="Zoom in">
                  <Plus className="size-4" />
                </button>
              </div>
              <div className="relative">
                <button
                  onClick={() => setHistoryOpen(!historyOpen)}
                  className="flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-[13px] font-semibold hover:border-ink-3"
                >
                  <History className="size-4" /> History
                </button>
                {historyOpen && (
                  <VersionHistory
                    date={activeDate}
                    tz={tz}
                    canEdit={canPlan}
                    offBoard={offBoard}
                    extended={extensions}
                    onRemoveExtension={(id) => {
                      setHistoryOpen(false);
                      menuActions.removeEvent(id);
                    }}
                    onClose={() => setHistoryOpen(false)}
                    onUndo={() => {
                      setHistoryOpen(false);
                      void run(() => api.undo(activeDate), { text: "Last change undone", tone: "ok" });
                    }}
                    onRestore={(id) => {
                      setHistoryOpen(false);
                      void run(() => api.removeEvent(id), { text: "Back on the board", tone: "ok" });
                    }}
                  />
                )}
              </div>
              {canPlan && (
                <>
                  <button
                    onClick={() => setCloseStand(cfg.stands.find((s) => s.active)!.id)}
                    className="flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-[13px] font-semibold hover:border-ink-3"
                  >
                    <Zap className="size-4 text-warn" /> What if…
                  </button>
                  <div className="relative flex">
                    <button
                      onClick={startOptimize}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        if (mode === null) setOptimizeMenu(true);
                      }}
                      disabled={mode !== null}
                      title="Optimize the day on the board. Right-click to optimize more days."
                      className="flex items-center gap-1.5 rounded-l-lg bg-brand py-1.5 pr-2.5 pl-3.5 text-[13px] font-bold text-brand-ink shadow-sm hover:brightness-95 disabled:opacity-60"
                    >
                      <Sparkles className="size-4" /> Optimize day
                    </button>
                    <button
                      onClick={() => setOptimizeMenu(!optimizeMenu)}
                      disabled={mode !== null}
                      aria-label="Optimize more days"
                      title="Optimize more days"
                      className="rounded-r-lg border-l border-brand-ink/20 bg-brand px-1.5 text-brand-ink shadow-sm hover:brightness-95 disabled:opacity-60"
                    >
                      <ChevronDown className="size-4" />
                    </button>
                    {optimizeMenu && <OptimizeDaysMenu date={activeDate} tz={tz} onPick={(n) => void startOptimizeDays(n)} onClose={() => setOptimizeMenu(false)} />}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1 border-b border-line bg-panel px-3">
            {(
              [
                ["stands", "Stands & Gates", <Plane key="i" className="size-4" />, tabCounts.stands, "turns"],
                ["counters", "Check-in", <Armchair key="i" className="size-4" />, tabCounts.counters, "departures"],
                ["belts", "Reclaim belts", <Luggage key="i" className="size-4" />, tabCounts.belts, "arrivals"],
                ["outbound", "Outbound belts", <Briefcase key="i" className="size-4" />, tabCounts.outbound, "departures"],
              ] as const
            ).map(([id, text, icon, n, unit]) => (
              <button
                key={id}
                onClick={() => {
                  setBoardView(id);
                  setHover(null);
                }}
                className={clsx(
                  "-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-[13px] font-semibold",
                  boardView === id ? "border-brand text-ink" : "border-transparent text-ink-3 hover:text-ink-2",
                )}
                title={`${n} ${unit}`}
              >
                {icon}
                {text}
                <span className="tabular rounded-full bg-panel-2 px-1.5 text-[11px] text-ink-3">{n}</span>
              </button>
            ))}
          </div>

          <div className="flex min-h-0 flex-1">
            <div className="relative flex min-w-0 flex-1 flex-col">
              <div className="min-h-0 flex-1">
                {res ? (
                  <ResourceTimeline
                    board={b}
                    config={cfg}
                    now={now}
                    ppm={ZOOMS[zoom] ?? 2}
                    kind={boardView as ResourceKind}
                    rows={res.rows}
                    items={res.items}
                    canEdit={canPlan && mode === null && !device.touch}
                    selected={selected}
                    focusAt={focusAt}
                    onSelect={setSelected}
                    onReject={(reason) => setToast({ text: reason, tone: "bad" })}
                    onHover={(turnId, x, y) => setHover(turnId ? { turnId, x, y } : null)}
                    onContext={(turnId, x, y, item) => setCtx({ turnId, x, y, item })}
                    onExtend={(item, until) => {
                      const resource = boardView as ResourceKind;
                      const turnId = extendTarget(b, resource, item.turnId, item.legIds)?.id;
                      if (turnId) extendTo(resource, turnId, until);
                    }}
                    onMove={(item, toRow) => {
                      const kind = boardView as ResourceKind;
                      menuActions.setResource(kind, item.legIds, kind === "counters" ? (cur) => movedCounters(cur, item.rowId, toRow) : toRow);
                    }}
                    emptyText={
                      boardView === "outbound"
                        ? "No outbound belts set up. An administrator can add them in Settings."
                        : "Nothing to show."
                    }
                  />
                ) : (
                <Timeline
                  onHover={(turnId, x, y) => setHover(turnId ? { turnId, x, y } : null)}
                  onContext={(turnId, x, y) => setCtx({ turnId, x, y, item: null })}
                  board={b}
                  config={cfg}
                  now={now}
                  ppm={ZOOMS[zoom] ?? 2}
                  selected={selected}
                  onSelect={setSelected}
                  canEdit={canPlan && mode === null && !device.touch}
                  preview={timelinePreview}
                  focusAt={focusAt}
                  look={look}
                  cameras={cameras.data?.turns}
                  onDrop={(turnId, standId) =>
                    run(() => api.move(activeDate, b.version.id, turnId, standId), {
                      text: `Moved to ${standId ?? "unallocated"}`,
                      tone: "ok",
                      undo: true,
                      reasonFor: { turnId, standId },
                    })
                  }
                  onReject={(reason) => setToast({ text: reason, tone: "bad" })}
                  onExtend={(turnId, until) => extendTo("stand", turnId, until)}
                  onRemoveClosure={(id) => run(() => api.removeClosure(id), { text: "Stand reopened", tone: "ok" })}
                  onCloseStand={(standId) => setCloseStand(standId)}
                  onDay={(d, at) => {
                    setDate(d === today ? undefined : d);
                    setSelected(null);
                    setMode(null);
                    setFocusAt(at);
                  }}
                />
                )}
              </div>
              {mode?.type === "optimize" && (
                <OptimizeSheet
                  preview={mode.preview}
                  loading={!mode.preview}
                  applying={busy}
                  onDiscard={() => setMode(null)}
                  onApply={() => {
                    const p = mode.preview!;
                    void run(
                      async () => {
                        await api.applyStands(
                          activeDate,
                          p.baseVersionId,
                          p.after.stands,
                          `Optimized day: ${p.diff.moves.length} moves, quality ${p.before.score.quality} → ${p.after.score.quality}`,
                        );
                        setMode(null);
                      },
                      { text: `Applied ${p.diff.moves.length} moves`, tone: "ok", undo: true },
                    );
                  }}
                />
              )}
              {mode?.type === "optimize-days" && (
                <OptimizeDaysSheet
                  days={mode.days}
                  count={mode.count}
                  tz={tz}
                  shown={activeDate}
                  onShow={(d) => setDate(d === today ? undefined : d)}
                  applying={busy}
                  onDiscard={() => setMode(null)}
                  onApply={() => {
                    const days = mode.days!.filter((d) => d.diff.moves.length);
                    const from = mode.days![0]!.date;
                    void run(
                      async () => {
                        const r = await api.applyDays(
                          days.map((d) => ({
                            date: d.date,
                            baseVersionId: d.baseVersionId,
                            stands: d.after.stands,
                            summary: `Optimized ${mode.count} days from ${from}: ${d.diff.moves.length} moves on this day, quality ${d.before.score.quality} → ${d.after.score.quality}`,
                          })),
                        );
                        setMode(null);
                        return r;
                      },
                      (r) => {
                        const n = r.days.reduce((a, d) => a + d.moves, 0);
                        const re = r.days.filter((d) => d.reworked).length;
                        return {
                          text: `Applied ${n} moves over ${r.days.filter((d) => d.moves).length} days${re ? `; ${re} re-optimized because the day before changed` : ""}. Undo a day from its History.`,
                          tone: "ok",
                        };
                      },
                    );
                  }}
                />
              )}
              {mode?.type === "recovery" && (
                <RecoverySheet
                  title="Recovery options"
                  preview={mode.preview}
                  loading={!mode.preview}
                  selected={mode.selected}
                  onSelect={(id) => setMode({ ...mode, selected: id })}
                  applying={busy}
                  onDiscard={() => setMode(null)}
                  onApply={() => {
                    const p = mode.preview!;
                    const o = p.options.find((x) => x.id === mode.selected)!;
                    void run(
                      async () => {
                        await api.recoveryApply(activeDate, p.baseVersionId, mode.disruption, o.id);
                        setMode(null);
                      },
                      { text: `${o.title} applied (${o.moves.length} moves)`, tone: "ok", undo: true },
                    );
                  }}
                />
              )}
            </div>

            {/* Beside the board on a wide screen; over it, as a slide-over, on a tablet. */}
            <aside
              className={clsx(
                "shrink-0 border-l border-line bg-panel",
                flightPanel
                  ? "fixed inset-y-0 right-0 z-40 w-[min(380px,100vw)] shadow-2xl lg:static lg:z-auto lg:w-[340px] lg:shadow-none"
                  : "hidden w-[340px] lg:block",
              )}
            >
              {flightPanel ? (
                flightPanel
              ) : (
                <AlertsPanel
                  board={b}
                  canEdit={canPlan && mode === null}
                  onFocus={(turnId, at) => {
                    setSelected(turnId);
                    setFocusAt(at);
                  }}
                  onFix={startRecovery}
                />
              )}
            </aside>
          </div>
        </>
      )}

      {hover && !ctx && <HoverCard board={b} config={cfg} turnId={hover.turnId} x={hover.x} y={hover.y} cameras={cameras.data?.turns} />}
      {ctx && (
        <ContextMenu
          board={b}
          config={cfg}
          turnId={ctx.turnId}
          item={ctx.item}
          kind={ctx.item ? (boardView as ResourceKind) : "stands"}
          x={ctx.x}
          y={ctx.y}
          canEdit={canPlan && mode === null}
          actions={menuActions}
          onClose={() => setCtx(null)}
        />
      )}
      {swapFor && b.turns.find((t) => t.id === swapFor) && (
        <SwapDialog
          board={b}
          config={cfg}
          turn={b.turns.find((t) => t.id === swapFor)!}
          onCancel={() => setSwapFor(null)}
          onSubmit={(choice) => {
            const turnId = swapFor;
            setSwapFor(null);
            const brought = "newTail" in choice ? choice.newTail.registration : null;
            void run(() => api.swapTails(activeDate, turnId, choice), { text: brought ? `${brought} brought in and swapped` : "Tails swapped", tone: "ok" });
          }}
        />
      )}
      {towFor && b.turns.find((t) => t.id === towFor) && (
        <TowDialog
          board={b}
          config={cfg}
          turn={b.turns.find((t) => t.id === towFor)!}
          now={now}
          onCancel={() => setTowFor(null)}
          onSettings={() => {
            setTowFor(null);
            setSettingsSection("texting");
            setView("settings");
          }}
          onSubmit={(at, toStand, note) => {
            const turnId = towFor;
            const airline = airlineName(b.turns.find((t) => t.id === turnId)?.airline ?? "", cfg);
            setTowFor(null);
            void run(
              () => api.tow(activeDate, turnId, at, toStand, note || undefined),
              (r) => ({ text: `Tow to ${toStand} planned. ${textedText(r.names, airline)}`, tone: "ok" }),
            );
          }}
        />
      )}
      {extendFor && b.turns.find((t) => t.id === extendFor.turnId) && (
        <ExtendDialog
          board={b}
          turn={b.turns.find((t) => t.id === extendFor.turnId)!}
          resource={extendFor.resource}
          onCancel={() => setExtendFor(null)}
          onRemove={(id) => {
            setExtendFor(null);
            menuActions.removeEvent(id);
          }}
          onSubmit={(until, note) => {
            const { turnId, resource } = extendFor;
            setExtendFor(null);
            extendTo(resource, turnId, until, note);
          }}
        />
      )}
      {mechFor && b.turns.find((t) => t.id === mechFor) && (
        <MechDialog
          board={b}
          turn={b.turns.find((t) => t.id === mechFor)!}
          onCancel={() => setMechFor(null)}
          onSubmit={(until, note) => {
            const turnId = mechFor;
            setMechFor(null);
            void run(() => api.mech(activeDate, turnId, until, note), { text: "Maintenance hold set", tone: "ok" });
          }}
        />
      )}

      {cancelFor && (b.turns.some((t) => t.id === cancelFor) || cancelledById(b, cancelFor)) && (
        <CancelDialog
          board={b}
          turn={b.turns.find((t) => t.id === cancelFor)}
          cancelled={cancelledById(b, cancelFor)}
          now={now}
          onCancel={() => setCancelFor(null)}
          onSubmit={(legId, bags, note) => {
            const gone = cancelledById(b, cancelFor);
            setCancelFor(null);
            if (gone?.eventId) {
              void run(() => api.setCancelBags(activeDate, gone.eventId!, bags), (r) => ({ text: r.summary, tone: "ok" }));
            } else {
              if (selected === cancelFor) setSelected(null);
              void run(() => api.cancelFlight(activeDate, legId, bags, note || undefined), (r) => ({ text: `${r.summary}. Undo it from the C1 row.`, tone: "ok" }));
            }
          }}
        />
      )}

      {closeStand && (
        <CloseStandDialog
          config={cfg}
          date={activeDate}
          now={now}
          standId={closeStand}
          onCancel={() => setCloseStand(null)}
          onSubmit={startRecovery}
        />
      )}

      {toast && (
        <div
          className={clsx(
            "fixed bottom-4 left-1/2 z-50 flex max-w-[calc(100vw-1.5rem)] -translate-x-1/2 flex-wrap items-center gap-x-3 gap-y-2 rounded-xl px-4 py-2.5 text-[13px] font-semibold shadow-2xl",
            toast.tone === "ok" ? "bg-ink text-panel" : "bg-bad text-white",
          )}
        >
          {toast.text}
          {toast.undoEvent && canPlan && (
            <button
              className="rounded-md bg-panel/15 px-2 py-0.5 text-brand hover:bg-panel/25"
              onClick={() => {
                const id = toast.undoEvent!;
                setToast(null);
                void run(() => api.removeEvent(id), { text: "Undone", tone: "ok" });
              }}
            >
              Undo
            </button>
          )}
          {toast.undo && canPlan && (
            <button
              className="rounded-md bg-panel/15 px-2 py-0.5 text-brand hover:bg-panel/25"
              onClick={() => {
                // One undo for however many versions the change made: undoing them one at
                // a time would redo, since each undo is itself a version to undo.
                const steps = toast.undoSteps ?? 1;
                setToast(null);
                void run(() => api.undo(activeDate, steps), { text: "Undone", tone: "ok" });
              }}
            >
              Undo
            </button>
          )}
          {toast.reasonFor && canPlan && (
            <MoveReasonChips
              onPick={(reason) => {
                const r = toast.reasonFor!;
                setToast({ text: "Thanks: the plan learns from it", tone: "ok" });
                api.moveReason(activeDate, r.turnId, r.standId, reason).catch(fail);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Why did you move it? One tap after a move, or "Other" to type it. Optional: the move is
 * already made, and the toast goes away on its own. What is said here is what the learning
 * puts beside the stand next time ("Planners put WS181 on Stand 7 by hand, why: customs").
 */
export function MoveReasonChips({ onPick }: { onPick: (reason: string) => void }) {
  const [other, setOther] = useState<string | null>(null);
  return (
    <span className="flex flex-wrap items-center gap-1 border-l border-panel/20 pl-3 text-[11.5px] font-normal">
      <span className="opacity-70">Why?</span>
      {other === null ? (
        <>
          {MOVE_REASONS.map((r) => (
            <button key={r} className="rounded-full bg-panel/15 px-2 py-0.5 hover:bg-panel/25" onClick={() => onPick(r)}>
              {r}
            </button>
          ))}
          <button className="rounded-full bg-panel/15 px-2 py-0.5 hover:bg-panel/25" onClick={() => setOther("")}>
            Other…
          </button>
        </>
      ) : (
        <form
          className="flex items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (other.trim()) onPick(other.trim());
          }}
        >
          <input
            autoFocus
            aria-label="Why it was moved"
            className="w-48 rounded-md bg-panel/15 px-2 py-0.5 text-inherit placeholder:opacity-60"
            placeholder="In a few words"
            maxLength={200}
            value={other}
            onChange={(e) => setOther(e.target.value)}
          />
          <button type="submit" className="rounded-md bg-panel/15 px-2 py-0.5 text-brand hover:bg-panel/25">
            Save
          </button>
        </form>
      )}
    </span>
  );
}
